// components
import { SideEditorPanel } from '@components/SideEditor/SideEditor';
import { useSideEditorContext } from '@components/SideEditor/utils';
import Tooltip from '@components/Tooltip/Tooltip';
import { emailSignatureSideEditorServicesContext } from '@context/email-signature-services-context';
// Context
import { sideEditorContext } from '@context/side-editor-context';
import { generateNewImageId } from '@modules/email-signatures-helpers';
import Emitter from '@modules/event-emitter';
import { useCallback, useContext, useEffect, useMemo } from 'react';

// types and consts
import { MenuItemsIndexDictionary } from '../../consts';
import {
  type EmailSignatureImagesTypes,
  EmailSignatureShapes,
  EmailSignatureSideEditorItems,
  EmailSignatureSizes,
} from '../../types';
import EmailSignatureEditorNewFieldButton from '../EmailSignatureEditorNewFieldButton';
import EmailSignatureEditorPanelNewFieldSection from '../EmailSignatureEditorPanelNewFieldsSection';
import EmailSignatureEditorPanelSection, {
  EmailSignatureEditorEmptyPanelSection,
  EmailSignatureEditorPanelSectionContents,
  EmailSignatureEditorPanelSectionHeading,
} from '../EmailSignatureEditorPanelSection';
import EmailSignatureLockLayer from '../EmailSignatureLockLayer';
import styles from './EmailSignatureEditorImagesPanel.module.scss';
import EmailSignatureEditorImageSection from './EmailSignatureImageSection';

const EmailSignatureEditorImagesPanel: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { emailSignature, emailSignatureDispatch, isInTransitionState, isSignatureLocked } = useContext(
    emailSignatureSideEditorServicesContext,
  );
  const { activeTab } = useContext(sideEditorContext);
  const { changeTab, panelIndex } = useSideEditorContext();

  const index = MenuItemsIndexDictionary[EmailSignatureSideEditorItems.IMAGES];
  const isSelected = index === activeTab;

  const handleAddNewField = useCallback(
    (imageType: EmailSignatureImagesTypes) => {
      if (emailSignature.images.length === 2) {
        return;
      }

      emailSignatureDispatch({
        type: 'ADD_IMAGE',
        value: {
          id: generateNewImageId(),
          externalLink: '',
          imageUrl: '',
          shape: EmailSignatureShapes.SQUARE,
          size: EmailSignatureSizes.MEDIUM,
          type: imageType,
          isPrimary: emailSignature.images.length === 0,
          originalImageUrl: '',
          crop: null,
        },
      });
    },
    [emailSignature.images.length, emailSignatureDispatch],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: historic
  useEffect(() => {
    const handleScrollToImagesPanel = () => {
      if (isInTransitionState) {
        return;
      }

      changeTab(index);
    };
    Emitter.on('SCROLL_TO_IMAGES_PANEL', handleScrollToImagesPanel);

    return () => {
      Emitter.off('SCROLL_TO_IMAGES_PANEL', handleScrollToImagesPanel);
    };
  }, [changeTab, index, isInTransitionState, panelIndex]);

  const disableProfilePicture = useMemo(
    () =>
      emailSignature.images.length === 2 ||
      (emailSignature.images.length === 1 && emailSignature.images[0].type === 'profile-picture'),
    [emailSignature.images],
  );

  const disableCompanyLogo = useMemo(
    () =>
      emailSignature.images.length === 2 ||
      (emailSignature.images.length === 1 && emailSignature.images[0].type === 'company-logo'),
    [emailSignature.images],
  );

  return (
    <SideEditorPanel index={index}>
      {emailSignature.images.length > 0 && (
        <EmailSignatureEditorPanelSection>
          <EmailSignatureEditorPanelSectionHeading heading={'Images'} />
          <EmailSignatureEditorPanelSectionContents>
            {emailSignature.images.map((image) => (
              <EmailSignatureEditorImageSection key={image.id} show={isSelected} image={image} />
            ))}
          </EmailSignatureEditorPanelSectionContents>{' '}
        </EmailSignatureEditorPanelSection>
      )}
      {emailSignature.images.length === 0 && (
        <EmailSignatureEditorEmptyPanelSection>
          <EmailSignatureEditorPanelSectionHeading heading="Images" />
          <EmailSignatureEditorPanelSectionContents>
            <div className={styles['add-new-images-message']}>
              Click the buttons below to add up to 2 new images
            </div>
          </EmailSignatureEditorPanelSectionContents>
        </EmailSignatureEditorEmptyPanelSection>
      )}

      <EmailSignatureEditorPanelNewFieldSection>
        <Tooltip
          size="small"
          label={
            emailSignature.images.length === 2
              ? 'Only two images can be added to your email signature'
              : 'Only one profile image can be added to your email signature'
          }
          aria-label=""
          show={disableProfilePicture}
        >
          <div>
            <EmailSignatureEditorNewFieldButton
              onActivate={handleAddNewField}
              type={'profile-picture'}
              subtitle="Profile image"
              disabled={disableProfilePicture || isSignatureLocked}
            />
          </div>
        </Tooltip>

        <Tooltip
          size="small"
          label={
            emailSignature.images.length === 2
              ? 'Only two images can be added to your email signature'
              : 'Only one company logo can be added to your email signature'
          }
          aria-label=""
          show={disableCompanyLogo}
        >
          <div>
            <EmailSignatureEditorNewFieldButton
              onActivate={handleAddNewField}
              type={'company-logo'}
              subtitle="Company logo"
              disabled={disableCompanyLogo || isSignatureLocked}
            />
          </div>
        </Tooltip>

        <Tooltip
          size="small"
          label="Only two images can be added to your email signature"
          aria-label=""
          show={emailSignature.images.length === 2}
        >
          <div>
            <EmailSignatureEditorNewFieldButton
              onActivate={handleAddNewField}
              type={'other'}
              subtitle="Other image"
              disabled={emailSignature.images.length === 2 || isSignatureLocked}
            />
          </div>
        </Tooltip>

        {isSignatureLocked && (
          <EmailSignatureLockLayer
            message={'You cannot add a new image, please contact your admin if you have any questions'}
          />
        )}
      </EmailSignatureEditorPanelNewFieldSection>
    </SideEditorPanel>
  );
};

export default EmailSignatureEditorImagesPanel;
